/**
 * People.tsx · 09_APP · OS-006 · 2026-10-20 · C43, v61
 *
 * Her words: "we need to be able to dip into the database for the women and then the CRM with all the people,
 * community connections... one tab so it doesn't get cluttered... you click on either community database or
 * client database. Because I was just thinking, how would I search for this person's profile?"
 *
 * One tab, two databases. Client database = the women C2C serves (enrollment or ambassador). Community
 * database = everyone else. Search by name, email, phone or organization; tap to open the profile.
 * Phone first (hard rule 18): 16px search box so iOS does not zoom, big rows, one column.
 */

import { useEffect, useRef, useState } from "react";
import { type PeopleResult } from "../lib/os";
import { os } from "../lib/os";
import { Profile } from "./Profile";

type Db = "client" | "community";

// A date-only value ("2026-10-20") is a calendar day, not an instant: reading it as UTC midnight showed it
// one day early in Sacramento. Found in the first live check, 2026-10-20.
const when = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T12:00:00") : new Date(iso);
  return d.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" });
};

export function People({ initialOpen = null, onClosed }: { initialOpen?: string | null; onClosed?: () => void } = {}) {
  const [db, setDb] = useState<Db | null>(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState<PeopleResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(initialOpen);
  useEffect(() => { if (initialOpen) setOpen(initialOpen); }, [initialOpen]);
  const timer = useRef<number | undefined>(undefined);
  const seq = useRef(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!db) return;
    window.clearTimeout(timer.current);
    setBusy(true);
    timer.current = window.setTimeout(async () => {
      const mine = ++seq.current;   // only the newest search may paint; older answers are dropped
      try { setErr(null); const r = await os.peopleSearch(db, q); if (mine === seq.current) { setRes(r); setBusy(false); } }
      catch (e) { if (mine === seq.current) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); } }
    }, 250);
    return () => window.clearTimeout(timer.current);
  }, [db, q]);

  if (open) return <Profile id={open} onBack={() => { setOpen(null); onClosed?.(); if (location.hash.startsWith("#people/")) history.replaceState(null, "", "#people"); }} />;

  if (!db) {
    return (
      <main className="people">
        <h2 className="sec">People</h2>
        <div className="pp-pick">
          <button onClick={() => setDb("client")}>
            <strong>Client database</strong>
            <span>The women we serve: applicants, students and alumni</span>
          </button>
          <button onClick={() => setDb("community")}>
            <strong>Community database</strong>
            <span>Everyone else: supporters, partners, funders, volunteers and people you meet</span>
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="people">
      <div className="pp-top">
        <button className="pp-back" onClick={() => { setDb(null); setQ(""); setRes(null); }}>‹ People</button>
        <h2 className="sec">{db === "client" ? "Client database" : "Community database"}</h2>
      </div>
      <input className="pp-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search name, email, phone or organization" autoComplete="off" autoCapitalize="none" />
      {err && <div className="refusebox">{err}</div>}
      {busy && <p className="pp-count">Searching…</p>}
      {res && !busy && (
        <p className="pp-count">
          {q.trim()
            ? `${res.results.length}${res.results.length >= 40 ? "+" : ""} found`
            : `${res.total.toLocaleString()} ${res.total === 1 ? "person" : "people"}. Newest first.`}
        </p>
      )}
      {res && res.total === 0 && db === "client" && (
        <div className="pp-empty">No women in the client database yet. Academy applications and the women's signup will land here.</div>
      )}
      <ul className="pp-list">
        {res?.results.map((r) => (
          <li key={r.id}>
            <button onClick={() => setOpen(r.id)}>
              <strong>{r.name}</strong>
              <span>{[r.organisation, r.email, r.phone].filter(Boolean).join(" · ")}</span>
              {r.source && <em>{r.source}{r.since ? `, ${when(r.since)}` : ""}</em>}
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}

